import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import confetti from 'canvas-confetti';
import PropTypes from 'prop-types';
import { Volume2, RotateCcw, Trophy, Star, Home, Shuffle, Target, Plus } from 'lucide-react';
import useAudio from '../../hooks/useAudio';
import useNumbersGame from '../../hooks/useNumbersGame';
import { Link } from 'react-router-dom';
import useAnalytics from '../../hooks/useAnalytics';

// Scoring constants
const SCORING_CONSTANTS = {
  BASE_POINTS: 20,
  STREAK_MULTIPLIER: 2,
  SPEED_BONUS_MAX: 80,
  ACCURACY_BONUS_MAX: 150,
  PERFECT_ACCURACY_BONUS: 200,
  LEVEL_COMPLETE_BONUS: 120,
  MAX_SPEED_BONUS_TIME: 20,
  COMBO_BONUS: 50
};

// Level configurations
const LEVEL_CONFIGS = {
  1: { range: [0, 5], gridSize: 4, targetsPerLevel: 8, targetRange: [3, 8], title: "Baby Sums", description: "Add small numbers!", passingScore: 75 },
  2: { range: [0, 8], gridSize: 4, targetsPerLevel: 10, targetRange: [5, 12], title: "Growing Sums", description: "Mix and match!", passingScore: 75 },
  3: { range: [0, 12], gridSize: 5, targetsPerLevel: 10, targetRange: [8, 20], title: "Dozen Challenge", description: "Bigger numbers!", passingScore: 80 },
  4: { range: [0, 20], gridSize: 5, targetsPerLevel: 12, targetRange: [15, 35], title: "Twenty Club", description: "Strategic thinking!", passingScore: 80 },
  5: { range: [0, 30], gridSize: 6, targetsPerLevel: 12, targetRange: [25, 60], title: "Math Wizard", description: "Complex combinations!", passingScore: 85 },
  6: { range: [0, 50], gridSize: 6, targetsPerLevel: 15, targetRange: [40, 120], title: "Sum Master", description: "Expert calculations!", passingScore: 85 },
  7: { range: [0, 100], gridSize: 7, targetsPerLevel: 15, targetRange: [80, 250], title: "Ultimate Calculator", description: "Master mathematician!", passingScore: 90 }
};

const ArabicNumberSumTargetGame = () => {
  const { 
    userId, level, setLevel, maxLevel, numbersData, bestScores, isLoading, error,
    saveLevelData, updateLevel, unlockNextLevel, resetAllProgress
  } = useNumbersGame('arabicNumberSumTarget');

  const {
    playCorrectSound, playIncorrectSound, playPerfectSound, playStreakSound,
    playWhooshSound, playStarSound, startBackgroundMusic, AudioElements
  } = useAudio();

  const audioRef = useRef(null);
  const audioTimeoutRef = useRef(null);
  
  // Game state
  const [gamePhase, setGamePhase] = useState('menu');
  const [currentTargetIndex, setCurrentTargetIndex] = useState(0);
  const [currentTarget, setCurrentTarget] = useState(0);
  const [gridNumbers, setGridNumbers] = useState([]);
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [foundCombinations, setFoundCombinations] = useState([]);

  const [sessionStartTime, setSessionStartTime] = useState(Date.now());
  const [sessionMistakes, setSessionMistakes] = useState([]);
  
  // Local level management to override the hook's maxLevel
  const [localMaxLevel, setLocalMaxLevel] = useState(1); // Start with only Level 1 unlocked
  
  // Scoring state
  const [totalScore, setTotalScore] = useState(0);
  const [correctTargets, setCorrectTargets] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [targetStartTime, setTargetStartTime] = useState(Date.now());
  const [speedBonuses, setSpeedBonuses] = useState(0);
  const [streakBonuses, setStreakBonuses] = useState(0);
  const [comboBonuses, setComboBonuses] = useState(0);
  const [stars, setStars] = useState(0);
  
  // UI state
  const [feedback, setFeedback] = useState('');
  const [celebrations, setCelebrations] = useState(false);
  const [sparkles, setSparkles] = useState(false);
  const [showLevelComplete, setShowLevelComplete] = useState(false);
  const [levelCompleteData, setLevelCompleteData] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [hintCombination, setHintCombination] = useState([]);
  
  // Initialize local max level from Firebase data
  const { saveSessionData } = useAnalytics();

  useEffect(() => {
    if (bestScores && Object.keys(bestScores).length > 0) {
      // Find the highest SEQUENTIALLY completed level with passing score
      let highestCompleted = 1;
      for (let i = 1; i <= 7; i++) {
        const levelScore = bestScores[`level${i}`];
        if (levelScore && levelScore.accuracy >= LEVEL_CONFIGS[i].passingScore) {
          highestCompleted = i + 1; // This level is complete, so unlock the next one
        } else {
          // Stop at first incomplete level - no skipping allowed
          break;
        }
      }
      // Cap at level 7 (don't unlock beyond the last level)
      setLocalMaxLevel(Math.min(highestCompleted, 7));
    } else {
      // No scores yet, start with level 1
      setLocalMaxLevel(1);
    }
  }, [bestScores]);

  const levelConfig = useMemo(() => LEVEL_CONFIGS[level] || LEVEL_CONFIGS[1], [level]);

  const shuffleArray = useCallback((array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  }, []);

  const playNumberAudio = useCallback(async (number) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    
    if (audioTimeoutRef.current) {
      clearTimeout(audioTimeoutRef.current);
      audioTimeoutRef.current = null;
    }

    const numberData = numbersData.find(n => n.englishNumeral === number);
    if (!numberData?.audioURL) return;

    setAudioPlaying(true);

    try {
      const audio = new Audio();
      audioRef.current = audio;
      
      audioTimeoutRef.current = setTimeout(() => {
        setAudioPlaying(false);
        audioRef.current = null;
      }, 5000);
      
      const handleEnded = () => {
        setAudioPlaying(false);
        if (audioTimeoutRef.current) {
          clearTimeout(audioTimeoutRef.current);
          audioTimeoutRef.current = null;
        }
        audioRef.current = null;
      };
      
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('error', handleEnded);
      audio.volume = 0.8;
      audio.src = numberData.audioURL;
      await audio.play();
      
    } catch (error) {
      setAudioPlaying(false);
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
        audioTimeoutRef.current = null;
      }
    }
  }, [numbersData]);

  const lastCelebrationRef = useRef(0);
  const triggerCelebration = useCallback((isStreak = false, earnedStars = 0, comboSize = 0) => {
    const now = Date.now();
    if (now - lastCelebrationRef.current < 500) return;
    lastCelebrationRef.current = now;
    
    setCelebrations(true);
    setSparkles(true);
    
    let colors = ['#00ff88', '#00ddff', '#8844ff'];
    let particleCount = 30;
    
    if (earnedStars > 0) {
      colors = ['#FFD700', '#FFA500'];
      particleCount = 50;
      playStarSound();
    } else if (isStreak) {
      colors = ['#FFD700', '#FFA500'];
      particleCount = 40;
      playStreakSound();
    } else {
      playCorrectSound();
    }
    
    confetti({ particleCount, spread: 50, origin: { y: 0.7 }, colors, gravity: 1.2 });
    setTimeout(() => { setCelebrations(false); setSparkles(false); }, 1500);
  }, [playStarSound, playStreakSound, playCorrectSound]);

  const calculateSpeedBonus = useCallback((responseTime) => {
    const maxTime = SCORING_CONSTANTS.MAX_SPEED_BONUS_TIME * 1000;
    if (responseTime <= maxTime) {
      return Math.max(0, Math.round(SCORING_CONSTANTS.SPEED_BONUS_MAX * (1 - responseTime / maxTime)));
    }
    return 0;
  }, []);

  const calculateStars = useCallback((accuracy, totalScore, totalTargets) => {
    const maxPossibleScore = totalTargets * (SCORING_CONSTANTS.BASE_POINTS + SCORING_CONSTANTS.SPEED_BONUS_MAX);
    const scorePercentage = (totalScore / maxPossibleScore) * 100;
    
    if (accuracy >= 95 && scorePercentage >= 85) return 3;
    if (accuracy >= 85 && scorePercentage >= 70) return 2;
    if (accuracy >= 70 && scorePercentage >= 50) return 1;
    return 0;
  }, []);

  const findAllCombinations = useCallback((numbers, target, minSize = 2, maxSize = 4) => {
    const combinations = [];
    
    const findCombos = (start, currentCombo, currentSum) => {
      if (currentSum === target && currentCombo.length >= minSize) {
        combinations.push([...currentCombo]);
        return;
      }
      
      if (currentSum >= target || currentCombo.length >= maxSize || start >= numbers.length) return;
      
      for (let i = start; i < numbers.length; i++) {
        const num = numbers[i];
        if (currentSum + num <= target) {
          findCombos(i + 1, [...currentCombo, { number: num, index: i }], currentSum + num);
        }
      }
    };
    
    findCombos(0, [], 0);
    return combinations;
  }, []);

  const generateTarget = useCallback(() => {
    if (!numbersData || numbersData.length === 0) return false;

    const [min, max] = levelConfig.range;
    const [targetMin, targetMax] = levelConfig.targetRange;
    const gridSize = levelConfig.gridSize;
    
    const availableNumbers = numbersData
      .filter(n => n.englishNumeral >= min && n.englishNumeral <= max)
      .map(n => n.englishNumeral);
    
    let attempts = 0, validTarget = false, newTarget, newGrid, possibleCombinations;
    
    while (!validTarget && attempts < 50) {
      newTarget = Math.floor(Math.random() * (targetMax - targetMin + 1)) + targetMin;
      newGrid = [];
      
      for (let i = 0; i < gridSize * gridSize; i++) {
        newGrid.push(availableNumbers[Math.floor(Math.random() * availableNumbers.length)]);
      }
      
      possibleCombinations = findAllCombinations(newGrid, newTarget);
      if (possibleCombinations.length > 0) validTarget = true;
      attempts++;
    }
    
    if (!validTarget) {
      newTarget = targetMin + 5;
      const combo1 = Math.floor(newTarget / 2);
      const combo2 = newTarget - combo1;
      newGrid = [combo1, combo2];
      
      for (let i = 2; i < gridSize * gridSize; i++) {
        newGrid.push(availableNumbers[Math.floor(Math.random() * availableNumbers.length)]);
      }
      newGrid = shuffleArray(newGrid);
    }
    
    setCurrentTarget(newTarget);
    setGridNumbers(newGrid);
    setSelectedNumbers([]);
    setFoundCombinations([]);
    setTargetStartTime(Date.now());
    setFeedback('');
    setShowHint(false);
    setHintCombination([]);
    
    return true;
  }, [levelConfig, numbersData, shuffleArray, findAllCombinations]);

  const handleLevelComplete = useCallback((finalCorrectTargets = null) => {
    const actualCorrectTargets = finalCorrectTargets !== null ? finalCorrectTargets : correctTargets;
    const totalTargets = levelConfig.targetsPerLevel;
    const accuracy = Math.round((actualCorrectTargets / totalTargets) * 100);
    const accuracyBonus = accuracy === 100 ? 
      SCORING_CONSTANTS.PERFECT_ACCURACY_BONUS : 
      Math.round((accuracy / 100) * SCORING_CONSTANTS.ACCURACY_BONUS_MAX);
    
    const finalScore = totalScore + accuracyBonus + SCORING_CONSTANTS.LEVEL_COMPLETE_BONUS;
    const earnedStars = calculateStars(accuracy, finalScore, totalTargets);
    
    const scoreData = {
      totalScore: finalScore, accuracy, stars: earnedStars, streak: bestStreak,
      speedBonuses, streakBonuses, comboBonuses, accuracyBonus,
      levelCompleteBonus: SCORING_CONSTANTS.LEVEL_COMPLETE_BONUS,
      completedAt: new Date().toISOString()
    };
    
    setStars(earnedStars);
    saveLevelData(level, scoreData);
    const sessionData = {
      gameType: 'arabicNumberSumTarget',
      level,
      sessionStartTime,
      sessionDuration: Math.round((Date.now() - sessionStartTime) / 1000),
      totalScore: finalScore,
      accuracy,
      attempts: levelConfig.targetsPerLevel,
      correctAnswers: actualCorrectTargets,
      mistakes: sessionMistakes,
      starsEarned: earnedStars,
      levelTitle: levelConfig.title,
      bestStreak,
      speedBonuses,
      streakBonuses,
      comboBonuses
    };
    saveSessionData(sessionData);
    
    const nextLevel = level + 1;
    const isLastLevel = !LEVEL_CONFIGS[nextLevel];
    const passedLevel = accuracy >= levelConfig.passingScore;
    
    setLevelCompleteData({
      ...scoreData, currentLevel: level, nextLevel, isLastLevel, passedLevel,
      levelTitle: levelConfig.title, passingScore: levelConfig.passingScore
    });
    
    if (earnedStars > 0) triggerCelebration(false, earnedStars);
    playPerfectSound();
    setGamePhase('complete');
    setShowLevelComplete(true);
    
    if (passedLevel && !isLastLevel) {
      unlockNextLevel();
      // Update local max level immediately
      const nextLevelNum = level + 1;
      setLocalMaxLevel(prev => Math.max(prev, nextLevelNum));
    }
  }, [correctTargets, levelConfig, totalScore, bestStreak, speedBonuses, streakBonuses, comboBonuses, level, calculateStars, saveLevelData, triggerCelebration, playPerfectSound, unlockNextLevel]);

  const nextTarget = useCallback(() => {
    const newIndex = currentTargetIndex + 1;
    if (newIndex >= levelConfig.targetsPerLevel) {
      handleLevelComplete(correctTargets);
    } else {
      setCurrentTargetIndex(newIndex);
      generateTarget();
      playWhooshSound();
    }
  }, [currentTargetIndex, levelConfig.targetsPerLevel, correctTargets, generateTarget, playWhooshSound, handleLevelComplete]);

  const skipTarget = useCallback(() => {
    setCurrentStreak(0);
    setFeedback('');
    playWhooshSound();
    
    const newIndex = currentTargetIndex + 1;
    if (newIndex >= levelConfig.targetsPerLevel) {
      handleLevelComplete(correctTargets);
    } else {
      setCurrentTargetIndex(newIndex);
      generateTarget();
    }
  }, [currentTargetIndex, levelConfig.targetsPerLevel, correctTargets, handleLevelComplete, generateTarget, playWhooshSound]);

  const handleNumberClick = useCallback((number, index) => {
    const isSelected = selectedNumbers.some(sel => sel.index === index);
    
    if (isSelected) {
      setSelectedNumbers(prev => prev.filter(sel => sel.index !== index));
    } else {
      setSelectedNumbers(prev => [...prev, { number, index }]);
    }
    setFeedback('');
  }, [selectedNumbers]);

  const checkCombination = useCallback(() => {
    if (selectedNumbers.length < 2) {
      setFeedback('Select at least 2 numbers!');
      setTimeout(() => setFeedback(''), 2000);
      return;
    }
    
    const sum = selectedNumbers.reduce((total, sel) => total + sel.number, 0);
    
    if (sum === currentTarget) {
      const responseTime = Date.now() - targetStartTime;
      const speedBonus = calculateSpeedBonus(responseTime);
      const comboBonus = selectedNumbers.length >= 4 ? SCORING_CONSTANTS.COMBO_BONUS : 0;
      const newStreak = currentStreak + 1;
      
      let points = SCORING_CONSTANTS.BASE_POINTS;
      if (newStreak >= 3) {
        points *= SCORING_CONSTANTS.STREAK_MULTIPLIER;
        setStreakBonuses(prev => prev + (SCORING_CONSTANTS.BASE_POINTS * (SCORING_CONSTANTS.STREAK_MULTIPLIER - 1)));
      }
      
      setCorrectTargets(prev => prev + 1);
      setCurrentStreak(newStreak);
      setBestStreak(prev => Math.max(prev, newStreak));
      setTotalScore(prev => prev + points + speedBonus + comboBonus);
      setSpeedBonuses(prev => prev + speedBonus);
      setComboBonuses(prev => prev + comboBonus);
      
      setFoundCombinations(prev => [...prev, [...selectedNumbers]]);
      setSelectedNumbers([]);
      setFeedback('correct');
      
      const isStreak = newStreak >= 3;
      triggerCelebration(isStreak, 0, selectedNumbers.length);
      
      const newCorrectCount = correctTargets + 1;
      if (newCorrectCount >= levelConfig.targetsPerLevel) {
        setTimeout(() => handleLevelComplete(newCorrectCount), 2000);
      } else {
        setTimeout(() => nextTarget(), 3000);
      }
    } else {
      setSessionMistakes(prev => [...prev, {
        timestamp: Date.now(),
        type: 'wrong_sum',
        attempted: selectedNumbers.map(s => s.number).join(' + '),
        attemptedSum: sum,
        correctTarget: currentTarget,
        targetIndex: currentTargetIndex
      }]);

      setFeedback(`Wrong! ${sum} ≠ ${currentTarget}`);
      playIncorrectSound();
      setSelectedNumbers([]);
      setTimeout(() => setFeedback(''), 2000);
    }
  }, [selectedNumbers, currentTarget, targetStartTime, currentStreak, correctTargets, levelConfig.targetsPerLevel, calculateSpeedBonus, triggerCelebration, playIncorrectSound, handleLevelComplete, nextTarget]);

  const showHintCombination = useCallback(() => {
    const allCombinations = findAllCombinations(gridNumbers, currentTarget);
    
    if (allCombinations.length > 0) {
      const availableCombos = allCombinations.filter(combo => 
        !foundCombinations.some(found => 
          found.length === combo.length && 
          found.every(f => combo.some(c => c.index === f.index))
        )
      );
      
      if (availableCombos.length > 0) {
        setHintCombination(availableCombos[0]);
        setShowHint(true);
        setTimeout(() => { setShowHint(false); setHintCombination([]); }, 5000);
      } else {
        setFeedback('All combinations found! Move to next target.');
        setTimeout(() => setFeedback(''), 2000);
      }
    } else {
      setFeedback('No valid combinations found!');
      setTimeout(() => setFeedback(''), 2000);
    }
  }, [gridNumbers, currentTarget, foundCombinations, findAllCombinations]);

  const startLevel = useCallback(() => {
    setSessionStartTime(Date.now());
    setSessionMistakes([]); // 
    setGamePhase('playing');
    setCurrentTargetIndex(0);
    setTotalScore(0);
    setCorrectTargets(0);
    setCurrentStreak(0);
    setBestStreak(0);
    setSpeedBonuses(0);
    setStreakBonuses(0);
    setComboBonuses(0);
    setStars(0);
    
    setTimeout(() => generateTarget(), 100);
  }, [generateTarget]);

  const goToMenu = useCallback(() => setGamePhase('menu'), []);
  const handleResetProgress = useCallback(() => setShowResetConfirmation(true), []);
  const confirmResetProgress = useCallback(() => {
    resetAllProgress();
    updateLevel(1);
    setLocalMaxLevel(1); // Reset local max level to 1
    setGamePhase('menu');
    setShowResetConfirmation(false);
  }, [resetAllProgress, updateLevel]);

  const handleProceedToNextLevel = useCallback(() => {
    if (levelCompleteData?.passedLevel && !levelCompleteData?.isLastLevel) {
      const nextLevelNum = levelCompleteData.nextLevel;
      if (nextLevelNum && nextLevelNum <= 7) {
        updateLevel(nextLevelNum);
      }
    }
    setShowLevelComplete(false);
    setLevelCompleteData(null);
    setGamePhase('menu');
  }, [levelCompleteData, updateLevel]);

  const selectLevel = useCallback((newLevel) => {
    // Only allow selecting levels that are actually unlocked
    if (newLevel <= localMaxLevel) {
      setLevel(newLevel);
    }
  }, [setLevel, localMaxLevel]);

  useEffect(() => {
    if (numbersData && numbersData.length > 0 && gamePhase === 'playing') {
      generateTarget();
    }
  }, [numbersData, gamePhase, generateTarget]);

  useEffect(() => {
    const musicTimer = setTimeout(() => startBackgroundMusic(), 1000);
    return () => clearTimeout(musicTimer);
  }, [startBackgroundMusic]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
        audioTimeoutRef.current = null;
      }
      confetti.reset();
    };
  }, []);

  const StarDisplay = React.memo(({ count, size = "text-2xl" }) => (
    <div className={`flex justify-center gap-1 ${size}`}>
      {[1, 2, 3].map(star => (
        <span key={star} className={`drop-shadow-lg transition-all duration-300 ${star <= count ? 'text-yellow-400 animate-pulse' : 'text-gray-300'}`}>
          ⭐
        </span>
      ))}
    </div>
  ));

  StarDisplay.propTypes = { count: PropTypes.number.isRequired, size: PropTypes.string };

  const NumberTile = React.memo(({ number, index, isSelected, isHinted, isUsed }) => {
    const numberData = numbersData.find(n => n.englishNumeral === number);
    if (!numberData) return null;

    return (
      <div
        onClick={() => !isUsed && handleNumberClick(number, index)}
        className={`relative w-20 h-20 md:w-24 md:h-24 rounded-2xl shadow-lg flex flex-col items-center justify-center text-white font-bold border-4 transition-all duration-200 cursor-pointer
          ${isUsed ? 'bg-gray-400 border-gray-500 opacity-50 cursor-not-allowed' : 
            isSelected ? 'bg-gradient-to-br from-green-400 to-green-500 border-green-600 scale-110' : 
            isHinted ? 'bg-gradient-to-br from-yellow-400 to-orange-400 border-yellow-500 animate-pulse' :
            'bg-gradient-to-br from-blue-400 to-purple-500 border-blue-600 hover:scale-105'}`}
      >
        <div className="text-2xl md:text-3xl mb-1">{numberData.arabicNumeral}</div>
        <div className="text-xs text-center px-1 leading-tight">{numberData.arabicWord}</div>
        
        {!isUsed && (
          <div className="absolute -top-2 -right-2">
            <button
              onClick={(e) => { e.stopPropagation(); playNumberAudio(number); }}
              disabled={audioPlaying}
              className="w-8 h-8 bg-yellow-400 text-yellow-800 rounded-full shadow-lg hover:bg-yellow-300 hover:scale-110 transition-all duration-200 border-2 border-white flex items-center justify-center"
            >
              <Volume2 size={16} />
            </button>
          </div>
        )}
        
        {isUsed && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-3xl">✓</span>
          </div>
        )}
      </div>
    );
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-400 via-teal-500 to-cyan-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-sm w-full">
          <div className="text-center">
            <div className="text-4xl md:text-6xl mb-4 animate-spin">🎯</div>
            <div className="text-purple-600 text-xl md:text-3xl font-bold animate-pulse">Loading Sum Target...</div>
            <div className="text-gray-600 text-sm md:text-lg mt-2">Getting ready for math fun!</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-400 via-teal-500 to-cyan-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl">
          <div className="text-4xl md:text-6xl mb-4">😢</div>
          <h2 className="text-xl md:text-3xl font-bold text-red-600 mb-4">Oops! Something went wrong</h2>
          <p className="text-gray-700 mb-6 text-sm md:text-lg">{error?.message || 'Something went wrong'}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-2xl font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            🔄 Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-400 via-teal-500 to-cyan-600 p-4">
      <AudioElements />
      
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-4 md:mb-8">
          <h1 className="text-3xl md:text-6xl font-black text-white mb-2 md:mb-4 drop-shadow-2xl">
            🎯 Sum Target Master 🎯
          </h1>
          <p className="text-lg md:text-2xl text-white font-bold opacity-90 drop-shadow-lg">
            Find numbers that add up to the target!
          </p>
        </div>

        {gamePhase === 'menu' && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 mb-4 md:mb-8 border-4 border-yellow-300">
              <h2 className="text-2xl md:text-4xl font-bold text-center mb-2 md:mb-4 text-purple-600">
                🎮 Level {level}: {levelConfig.title}
              </h2>
              <p className="text-center text-gray-700 mb-4 md:mb-6 text-lg md:text-xl font-semibold">
                {levelConfig.description}
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-4 md:mb-8">
                <div className="text-center bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🔢</div>
                  <p className="font-bold text-sm md:text-lg">Numbers</p>
                  <p className="text-lg md:text-2xl font-black">{levelConfig.range[0]}-{levelConfig.range[1]}</p>
                </div>
                <div className="text-center bg-gradient-to-br from-green-400 to-green-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🎯</div>
                  <p className="font-bold text-sm md:text-lg">Targets</p>
                  <p className="text-lg md:text-2xl font-black">{levelConfig.targetsPerLevel}</p>
                </div>
                <div className="text-center bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">📊</div>
                  <p className="font-bold text-sm md:text-lg">Grid Size</p>
                  <p className="text-lg md:text-2xl font-black">{levelConfig.gridSize}×{levelConfig.gridSize}</p>
                </div>
                <div className="text-center bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🏆</div>
                  <p className="font-bold text-sm md:text-lg">Pass</p>
                  <p className="text-lg md:text-2xl font-black">{levelConfig.passingScore}%</p>
                </div>
              </div>

              <div className="text-center">
                <button
                  onClick={startLevel}
                  className="px-8 md:px-12 py-4 md:py-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-3xl font-black text-xl md:text-3xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300 border-4 border-white"
                >
                  🚀 START CALCULATING! 🚀
                </button>
              </div>
            </div>

            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 border-4 border-pink-300">
              <h3 className="text-xl md:text-3xl font-bold text-center mb-4 md:mb-6 text-purple-600">
                🎮 Choose Your Level!
              </h3>
              <div className="grid grid-cols-4 md:grid-cols-7 gap-2 md:gap-4">
                {Object.entries(LEVEL_CONFIGS).map(([levelNum, config]) => {
                  const num = parseInt(levelNum);
                  const isUnlocked = num <= localMaxLevel;
                  const isCurrent = num === level;
                  const levelScore = bestScores[`level${num}`];
                  
                  return (
                    <button
                      key={num}
                      onClick={() => {
                        if (isUnlocked && num !== level) {
                          selectLevel(num);
                        }
                      }}
                      disabled={!isUnlocked}
                      className={`relative p-2 md:p-4 rounded-2xl text-sm md:text-lg font-bold transition-all duration-300 min-h-[80px] md:min-h-[120px] flex flex-col items-center justify-center border-2 md:border-4
                        ${!isUnlocked ? 'bg-gray-300 text-gray-500 cursor-not-allowed border-gray-400 opacity-60' :
                          isCurrent ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-2xl transform scale-105 md:scale-110 border-yellow-300' : 
                          'bg-gradient-to-br from-green-100 to-green-200 text-green-800 hover:from-green-200 hover:to-green-300 cursor-pointer hover:scale-105 border-green-300'}`}
                      title={!isUnlocked ? `🔒 Complete Level ${num - 1} to unlock` : 
                             isCurrent ? `Currently playing Level ${num}` :
                             config.title}
                    >
                      <div className="text-lg md:text-2xl font-black">{num}</div>
                      <div className="text-xs mt-1 font-bold hidden md:block">
                        {isUnlocked ? `${config.gridSize}×${config.gridSize}` : 'Locked'}
                      </div>
                      <div className="text-xs font-bold md:hidden">
                        {isUnlocked ? `${config.gridSize}×${config.gridSize}` : '🔒'}
                      </div>
                      
                      {/* Stars for completed levels */}
                      {levelScore && isUnlocked && (
                        <div className="absolute -top-1 -right-1 md:-top-2 md:-right-2">
                          <StarDisplay count={levelScore.stars || 0} size="text-xs md:text-sm" />
                        </div>
                      )}
                      
                      {/* Lock overlay for locked levels */}
                      {!isUnlocked && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-400 bg-opacity-90 rounded-2xl">
                          <div className="text-center">
                            <div className="text-2xl md:text-3xl mb-1">🔒</div>
                            <div className="text-xs font-bold hidden md:block">Level {num - 1}</div>
                            <div className="text-xs font-bold hidden md:block">Required</div>
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              
              <div className="mt-4 md:mt-8 flex gap-2 md:gap-4 justify-center flex-wrap">
                <Link to="/">
                  <button 
                    className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  >
                    🔙 Main Menu
                  </button>
                </Link>
                <button 
                  onClick={handleResetProgress}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🗑️ Reset Progress
                </button>
                <button 
                  onClick={() => setShowInstructions(true)}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  📖 How to Play
                </button>
              </div>
            </div>
          </div>
        )}

        {gamePhase === 'playing' && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 md:p-6 mb-6 shadow-xl">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-xl md:text-2xl font-bold text-purple-600">
                    Target {currentTargetIndex + 1} of {levelConfig.targetsPerLevel}
                  </h3>
                  <p className="text-sm md:text-base text-gray-600 font-semibold">
                    🎮 Level {level}: {levelConfig.title}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-indigo-600">{totalScore} points</p>
                  <p className="text-sm text-gray-600">{correctTargets} correct</p>
                </div>
              </div>
              
              <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
                <div 
                  className="bg-gradient-to-r from-green-400 to-green-500 h-4 rounded-full transition-all duration-500"
                  style={{ width: `${(currentTargetIndex / levelConfig.targetsPerLevel) * 100}%` }}
                ></div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">🎯</div>
                  <p className="font-bold text-lg md:text-xl">{totalScore}</p>
                  <p className="text-xs font-bold">Score</p>
                </div>
                <div className="bg-gradient-to-br from-green-400 to-green-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">✅</div>
                  <p className="font-bold text-lg md:text-xl">{correctTargets}/{levelConfig.targetsPerLevel}</p>
                  <p className="text-xs font-bold">Complete</p>
                </div>
                <div className="bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">🔥</div>
                  <p className="font-bold text-lg md:text-xl">{currentStreak}</p>
                  <p className="text-xs font-bold">Streak</p>
                </div>
                <div className="bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">🏆</div>
                  <p className="font-bold text-lg md:text-xl">{bestStreak}</p>
                  <p className="text-xs font-bold">Best</p>
                </div>
              </div>
              
              {currentStreak >= 3 && (
                <div className="mt-4 text-center bg-gradient-to-r from-orange-400/80 to-red-400/80 rounded-xl p-3">
                  <p className="text-white font-bold text-lg animate-pulse">
                    🔥 {currentStreak} in a row! You're on fire! 🔥
                  </p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 mb-6 border-4 border-yellow-300">
              <div className="text-center mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
                  🎯 Find numbers that add up to:
                </h2>
                <div className="bg-gradient-to-br from-red-400 to-pink-500 text-white rounded-3xl p-6 md:p-8 mx-auto max-w-sm shadow-2xl relative">
                  <div className="text-6xl md:text-8xl font-black mb-2">
                    {(() => {
                      const targetData = numbersData.find(n => n.englishNumeral === currentTarget);
                      if (targetData && targetData.arabicNumeral) {
                        return targetData.arabicNumeral;
                      }
                      // Fallback: convert English number to Arabic numerals
                      return currentTarget.toString().replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[d]);
                    })()}
                  </div>
                  <p className="text-lg md:text-xl font-bold">TARGET SUM</p>
                  
                  {/* Speaker button for target number */}
                  <div className="absolute -top-4 -right-4">
                    <button
                      onClick={() => playNumberAudio(currentTarget)}
                      disabled={audioPlaying}
                      className="w-16 h-16 bg-yellow-400 text-yellow-800 rounded-full shadow-xl hover:bg-yellow-300 hover:scale-110 transition-all duration-200 border-4 border-white flex items-center justify-center"
                    >
                      <Volume2 size={28} />
                    </button>
                  </div>
                </div>
                
                {showHint && hintCombination.length > 0 && (
                  <div className="mt-4 p-4 bg-yellow-100 border-4 border-yellow-400 rounded-2xl">
                    <p className="text-yellow-800 font-bold text-lg mb-2">💡 Hint: Try these numbers!</p>
                    <div className="flex justify-center gap-2 flex-wrap">
                      {hintCombination.map((item, idx) => (
                        <span key={idx} className="bg-yellow-400 text-yellow-800 px-3 py-1 rounded-full font-bold">
                          {item.number}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className={`grid gap-3 md:gap-4 mb-6 justify-items-center`}
                   style={{ gridTemplateColumns: `repeat(${levelConfig.gridSize}, minmax(0, 1fr))` }}>
                {gridNumbers.map((number, index) => {
                  const isSelected = selectedNumbers.some(sel => sel.index === index);
                  const isHinted = showHint && hintCombination.some(hint => hint.index === index);
                  const isUsed = foundCombinations.some(combo => combo.some(item => item.index === index));
                  
                  return (
                    <NumberTile
                      key={index}
                      number={number}
                      index={index}
                      isSelected={isSelected}
                      isHinted={isHinted}
                      isUsed={isUsed}
                    />
                  );
                })}
              </div>

              {selectedNumbers.length > 0 && (
                <div className="mb-6 p-4 bg-blue-100 rounded-2xl border-4 border-blue-300">
                  <h3 className="text-lg font-bold text-blue-800 mb-2 text-center">Selected Numbers:</h3>
                  <div className="flex justify-center items-center gap-2 flex-wrap mb-2">
                    {selectedNumbers.map((sel, idx) => (
                      <React.Fragment key={sel.index}>
                        <span className="bg-blue-400 text-white px-3 py-1 rounded-full font-bold">
                          {sel.number}
                        </span>
                        {idx < selectedNumbers.length - 1 && (
                          <Plus size={16} className="text-blue-600" />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                  <p className="text-center text-blue-800 font-bold">
                    Sum: {selectedNumbers.reduce((sum, sel) => sum + sel.number, 0)}
                  </p>
                </div>
              )}

              {feedback && (
                <div className={`text-center mb-6 p-4 rounded-2xl border-4 ${
                  feedback === 'correct' ? 'text-green-600 bg-green-100 border-green-300' : 
                  feedback.includes('Wrong') ? 'text-red-600 bg-red-100 border-red-300' :
                  'text-orange-600 bg-orange-100 border-orange-300'
                }`}>
                  <div className={`text-3xl mb-2 ${feedback === 'correct' ? 'animate-bounce' : 'animate-pulse'}`}>
                    {feedback === 'correct' ? '🎉' : feedback.includes('Wrong') ? '❌' : '⚠️'}
                  </div>
                  <p className="text-xl font-bold">
                    {feedback === 'correct' ? 'Perfect! Great calculation!' : feedback}
                  </p>
                </div>
              )}

              <div className="flex gap-4 justify-center flex-wrap">
                <button 
                  onClick={checkCombination}
                  disabled={selectedNumbers.length < 2 || feedback !== ''}
                  className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Target size={20} />
                  Check Sum
                </button>
                <button 
                  onClick={showHintCombination}
                  className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-2xl font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  💡 Hint
                </button>
                <button 
                  onClick={skipTarget}
                  className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  ⏭️ Skip
                </button>
              </div>
            </div>

            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-xl">
              <div className="flex gap-3 md:gap-4 justify-center flex-wrap">
                <button 
                  onClick={goToMenu}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  <Home size={20} />
                  Menu
                </button>
                <button 
                  onClick={startLevel}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  <RotateCcw size={20} />
                  Restart Level
                </button>
              </div>
            </div>
          </div>
        )}

        {celebrations && (
          <div className="fixed inset-0 pointer-events-none z-40">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-6xl md:text-9xl animate-bounce">🎉</div>
            </div>
          </div>
        )}

        {sparkles && (
          <div className="fixed inset-0 pointer-events-none z-40">
            {[...Array(15)].map((_, i) => (
              <div
                key={`sparkle-${i}`}
                className="absolute animate-ping text-yellow-400 text-2xl"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`
                }}
              >
                ✨
              </div>
            ))}
          </div>
        )}
      </div>

      {showInstructions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-4 border-purple-300">
            <div className="p-4 md:p-8">
              <div className="flex justify-between items-center mb-4 md:mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-purple-600">📖 How to Play Sum Target!</h2>
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl md:text-3xl font-bold w-10 h-10 flex items-center justify-center"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 md:space-y-6">
                <div className="bg-gradient-to-r from-blue-100 to-blue-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-blue-300">
                  <h3 className="font-bold text-blue-800 mb-2 md:mb-3 text-lg md:text-xl">🎯 Your Mission</h3>
                  <p className="text-blue-700 text-sm md:text-lg">Find combinations of Arabic numbers that add up to the target sum! You can use 2-4 numbers in each combination.</p>
                </div>
                
                <div className="bg-gradient-to-r from-green-100 to-green-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-green-300">
                  <h3 className="font-bold text-green-800 mb-2 md:mb-3 text-lg md:text-xl">🎮 How to Play</h3>
                  <ul className="list-disc list-inside text-green-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li>Look at the target number at the top</li>
                    <li>Click on 2-4 numbers from the grid that add up to the target</li>
                    <li>Selected numbers will be highlighted in green</li>
                    <li>Click "Check Sum" to verify your combination</li>
                    <li>Find the combination to move to the next target!</li>
                    <li>Complete all targets to finish the level</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-yellow-100 to-yellow-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-yellow-300">
                  <h3 className="font-bold text-yellow-800 mb-2 md:mb-3 text-lg md:text-xl">🔊 Audio Help</h3>
                  <ul className="list-disc list-inside text-yellow-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li>Tap the speaker button on any number to hear its pronunciation</li>
                    <li>This helps you learn Arabic number names while calculating</li>
                    <li>Each number shows both Arabic and Western numerals</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-purple-100 to-purple-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-purple-300">
                  <h3 className="font-bold text-purple-800 mb-2 md:mb-3 text-lg md:text-xl">🎮 Game Controls</h3>
                  <ul className="list-disc list-inside text-purple-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li><strong>Check Sum:</strong> Verify if your selected numbers add up correctly</li>
                    <li><strong>Hint:</strong> Get a clue showing one possible combination</li>
                    <li><strong>Skip:</strong> Move to the next target if you're stuck</li>
                    <li>Click numbers again to deselect them</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-red-100 to-red-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-red-300">
                  <h3 className="font-bold text-red-800 mb-2 md:mb-3 text-lg md:text-xl">💡 Pro Tips</h3>
                  <ul className="list-disc list-inside text-red-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li>Start with the largest numbers and work your way down</li>
                    <li>Look for pairs that are close to half the target</li>
                    <li>Use 3-4 numbers for larger combinations and bonus points</li>
                    <li>Practice mental math to get faster at calculations</li>
                    <li>Use hints wisely - they show you possible solutions</li>
                    <li>Build streaks of correct answers for bonus points!</li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-4 md:mt-8 text-center">
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Let's Start Calculating! 🎯
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showLevelComplete && levelCompleteData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-4 md:p-8 border-4 border-yellow-300">
            <div className="text-center">
              <div className="text-6xl md:text-8xl mb-4 md:mb-6">
                {levelCompleteData.passedLevel ? '🎉' : '😢'}
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-purple-600 mb-2 md:mb-3">
                Level {levelCompleteData.currentLevel} Complete!
              </h2>
              <h3 className="text-lg md:text-xl text-gray-700 mb-4 md:mb-6 font-semibold">
                {levelCompleteData.levelTitle}
              </h3>
              
              <div className="mb-6 md:mb-8">
                <StarDisplay count={levelCompleteData.stars} size="text-3xl md:text-4xl" />
                <p className="text-xl md:text-2xl font-bold text-indigo-600 mt-3 md:mt-4">
                  Final Score: {levelCompleteData.totalScore}
                </p>
                <p className="text-base md:text-lg text-gray-600 mt-2">
                  Accuracy: {levelCompleteData.accuracy}% • Best Streak: {levelCompleteData.streak}
                </p>
              </div>

              <div className="mb-6 md:mb-8">
                {levelCompleteData.passedLevel ? (
                  <div className="bg-gradient-to-r from-green-100 to-green-200 border-2 md:border-4 border-green-400 rounded-2xl p-4 md:p-6">
                    <p className="text-green-800 font-bold text-lg md:text-xl">
                      ✅ Fantastic! You passed! 🎉
                    </p>
                    <p className="text-green-700 text-base md:text-lg">
                      {levelCompleteData.accuracy}% ≥ {levelCompleteData.passingScore}%
                    </p>
                    {!levelCompleteData.isLastLevel && (
                      <p className="text-green-700 text-base md:text-lg mt-2">Ready for level {levelCompleteData.nextLevel}?</p>
                    )}
                  </div>
                ) : (
                  <div className="bg-gradient-to-r from-red-100 to-red-200 border-2 md:border-4 border-red-400 rounded-2xl p-4 md:p-6">
                    <p className="text-red-800 font-bold text-lg md:text-xl">
                      😔 Almost there! Keep trying!
                    </p>
                    <p className="text-red-700 text-base md:text-lg">
                      You got {levelCompleteData.accuracy}% but need {levelCompleteData.passingScore}% to pass
                    </p>
                    <p className="text-red-700 text-base md:text-lg mt-2">Don't give up! Practice makes perfect! 💪</p>
                  </div>
                )}
              </div>

              <div className="space-y-3 md:space-y-4">
                <button 
                  onClick={() => { setShowLevelComplete(false); setGamePhase('menu'); }}
                  className="w-full px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🔄 Play Again
                </button>
                
                {levelCompleteData.passedLevel && !levelCompleteData.isLastLevel && (
                  <button 
                    onClick={handleProceedToNextLevel}
                    className="w-full px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300"
                  >
                    🚀 Next Level! 🚀
                  </button>
                )}
                
                {levelCompleteData.isLastLevel && (
                  <button 
                    onClick={() => { setShowLevelComplete(false); setGamePhase('menu'); }}
                    className="w-full px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300"
                  >
                    🎊 You're a Sum Master! 🎊
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showResetConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-4 md:p-8 border-4 border-red-300">
            <div className="text-center">
              <div className="text-6xl md:text-8xl mb-4 md:mb-6">⚠️</div>
              <h2 className="text-2xl md:text-3xl font-bold text-red-600 mb-3 md:mb-4">Reset Everything?</h2>
              <p className="text-gray-700 mb-6 md:mb-8 text-sm md:text-lg">
                This will delete all your amazing scores, stars, and progress. You'll start from Level 1 again.
                This cannot be undone!
              </p>
              <div className="flex gap-3 md:gap-4 justify-center">
                <button 
                  onClick={() => setShowResetConfirmation(false)}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmResetProgress}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Yes, Reset Everything
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArabicNumberSumTargetGame;